/**
 * @fileoverview Visitor data export utilities.
 * Provides functions for exporting visitor and interaction data to Excel (XLS) format,
 * with spreadsheet injection protection and formatting.
 * @module helperMethods/visitors/index
 */

import _ from "lodash";
import XLSX from "xlsx";
import moment from "moment";

/**
 * Builds worksheet rows for visitor data with headers and formatted fields.
 * Includes spreadsheet injection protection for cell content.
 *
 * @private
 * @param {any} fields - Field configuration with displayValue and dataType
 * @param {Array<{RowKey: string, Timestamp: any}>} visitors - Array of visitor objects
 * @returns {string[][]} 2D array of worksheet data
 */
const visitorRowsBuilder = (fields: any, visitors: { RowKey: string; Timestamp: any }[]) => {
  const ws_data: string[][] = [];
  ws_data.push(["ChatBot Visitors"]);
  ws_data.push([]);
  ws_data.push(["RowKey", "Last Seen", ..._.map(fields, (a) => a.displayValue)]);
  _.forEach(visitors, (visitor) => {
    const ws_head_row: string[] = [];
    ws_head_row.push(escapeSpreadsheetContent(visitor.RowKey));
    ws_head_row.push(escapeSpreadsheetContent(moment(visitor.Timestamp).format("DD/MM/YYYY:HH:mm:ss")));
    _.forEach(_.keys(fields), (field) => {
      const rowData = _.get(visitor, `${field}`, "");
      if (typeof rowData === "object") {
        ws_head_row.push(escapeSpreadsheetContent(JSON.stringify(rowData)));
      } else if (fields[field].dataType === "datetime" && rowData !== "") {
        ws_head_row.push(escapeSpreadsheetContent(moment(rowData).format("DD/MM/YYYY")));
      } else {
        ws_head_row.push(escapeSpreadsheetContent(rowData));
      }
    });
    ws_data.push(ws_head_row);
  });
  ws_data.push([]);

  return ws_data;
};

/**
 * Escapes potentially dangerous spreadsheet content to prevent formula injection.
 * Prepends a single quote to content starting with =, +, -, or @.
 *
 * @param {string} content - Content to escape
 * @returns {string} Escaped content safe for spreadsheet cells
 *
 * @example
 * escapeSpreadsheetContent('=SUM(A1:A10)');
 * // Returns: "'=SUM(A1:A10)"
 *
 * @example
 * escapeSpreadsheetContent('Normal text');
 * // Returns: "Normal text"
 */
function escapeSpreadsheetContent(content: string): string {
  if (!content) return "";
  const stringContent = String(content);
  // Escape content starting with =, +, -, @ by prepending a single quote
  if (/^[=+\-@]/.test(stringContent)) {
    return `'${stringContent}`;
  }
  return stringContent;
}

/**
 * Exports visitor data to an Excel workbook (XLS format).
 * Creates a worksheet with visitor information formatted with fields and timestamps.
 *
 * @param {any} fields - Field configuration with displayValue and dataType
 * @param {Array<{RowKey: string, Timestamp: any}>} visitors - Array of visitor objects to export
 * @returns {XLSX.WorkBook} Excel workbook ready for download
 *
 * @example
 * const workbook = exportUsersToXLS(fields, visitorsArray);
 * XLSX.writeFile(workbook, 'visitors.xlsx');
 */
export function exportUsersToXLS(
  fields: any,
  visitors: { RowKey: string; Timestamp: any }[]
): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const ws_data = visitorRowsBuilder(fields, visitors);
  const ws = XLSX.utils.aoa_to_sheet(ws_data);
  XLSX.utils.book_append_sheet(wb, ws, "ChatBot Visitors");
  return wb;
}

/**
 * Exports visitor interactions to an Excel workbook with separate sheets per user.
 * Each sheet contains visitor details followed by chronological interaction log.
 *
 * @param {any} fields - Field configuration with displayValue and dataType
 * @param {any} result - Interaction data keyed by userId
 * @param {any} rowData - Visitor row data keyed by userId
 * @returns {XLSX.WorkBook} Excel workbook with multiple sheets (one per visitor)
 *
 * @example
 * const workbook = exportInteractionsToXLS(fields, interactions, visitors);
 * XLSX.writeFile(workbook, 'interactions.xlsx');
 * // Creates a workbook with sheets named by userId containing interaction logs
 */
export function exportInteractionsToXLS(fields: any, result: any, rowData: any): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  _.forEach(_.keys(result), (userId: any) => {
    const visitor: { RowKey: string; Timestamp: any } = rowData[userId][0];
    const ws_data = visitorRowsBuilder(fields, [visitor]);

    ws_data.push(["Time", "Action", "Content"]);
    _.forEach(result[userId], (row: any) => {
      const ws_row: string[] = [];
      ws_row.push(moment(row.Timestamp).format("DD/MM/YYYY:HH:mm:ss"));
      ws_row.push(escapeSpreadsheetContent(row.event));
      ws_row.push(escapeSpreadsheetContent(row.data));
      ws_data.push(ws_row);
    });
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    XLSX.utils.book_append_sheet(wb, ws, userId.slice(0, 31));
  });

  return wb;
}
